import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';

// ─── Shared upload zone ──────────────────────────────────────────────────────

const FileUploadZone = ({ label, hint, currentUrl, onUpload, onRemove, uploading, accept = 'image/*', isImage = true }) => {
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e?.preventDefault();
    if (uploading) return;
    const file = e?.dataTransfer?.files?.[0];
    if (file) onUpload(file);
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{label}</label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}

      <div
        className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors relative ${
          uploading
            ? 'border-primary/50 bg-muted/30 cursor-wait' :'border-border cursor-pointer hover:border-primary/50 hover:bg-muted/30'
        }`}
        onClick={() => { if (!uploading) inputRef?.current?.click(); }}
        onDrop={handleDrop}
        onDragOver={(e) => e?.preventDefault()}
      >
        {uploading && (
          <div className="absolute inset-0 rounded-lg bg-background/70 flex flex-col items-center justify-center z-10 gap-2">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
            <p className="text-xs font-medium text-primary">Subiendo...</p>
          </div>
        )}

        {currentUrl && isImage ? (
          <div className="space-y-2">
            <img src={currentUrl} alt={label} className="max-h-32 mx-auto object-contain rounded border border-border" />
            <p className="text-xs text-muted-foreground">Haz clic o arrastra para reemplazar</p>
          </div>
        ) : currentUrl && !isImage ? (
          <div className="flex items-center justify-center gap-2 py-2">
            <Icon name="FileCheck" size={24} className="text-success" />
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">Archivo subido</p>
              <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline" onClick={(e) => e?.stopPropagation()}>
                Ver archivo
              </a>
            </div>
          </div>
        ) : (
          <div className="space-y-2 py-2">
            <Icon name="Upload" size={28} className="mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Arrastra un archivo o haz clic para seleccionar</p>
            <p className="text-xs text-muted-foreground">PNG, JPG, PDF hasta 10MB</p>
          </div>
        )}
      </div>

      {currentUrl && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-destructive hover:underline flex items-center gap-1"
        >
          <Icon name="Trash2" size={12} />
          Eliminar archivo
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e?.target?.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
      />
    </div>
  );
};

// ─── Proposal card ───────────────────────────────────────────────────────────

const ProposalCard = ({ id, label, description, selected, onSelect, branding }) => {
  const primary = branding?.primary_color || '#E53E3E';
  const secondary = branding?.secondary_color || '#1A1A1A';
  const logo = branding?.logo_url;
  const name = branding?.business_name || 'Tu empresa';

  return (
    <div
      className={`border-2 rounded-xl overflow-hidden cursor-pointer transition-all ${
        selected ? 'border-primary shadow-md' : 'border-border hover:border-primary/50'
      }`}
      onClick={() => onSelect(id)}
    >
      {/* Mini preview */}
      <div className="h-40 overflow-hidden bg-white relative">
        {id === 'propuesta_a' && (
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ backgroundColor: primary, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {logo ? (
                <img src={logo} alt="logo" style={{ height: '24px', width: 'auto', objectFit: 'contain' }} />
              ) : (
                <div style={{ width: '24px', height: '24px', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '3px' }} />
              )}
              <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '11px' }}>{name}</span>
            </div>
            <div style={{ backgroundColor: '#f5f5f5', borderBottom: `2px solid ${primary}`, padding: '4px 12px', fontSize: '9px', color: '#555' }}>
              Taller Mecánico Automotriz
            </div>
            <div style={{ flex: 1, padding: '8px 12px', borderLeft: `3px solid ${primary}`, margin: '6px 10px', backgroundColor: '#fafafa' }}>
              <div style={{ height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', marginBottom: '4px', width: '70%' }} />
              <div style={{ height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', marginBottom: '4px', width: '50%' }} />
              <div style={{ height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', width: '60%' }} />
            </div>
            <div style={{ backgroundColor: secondary, padding: '5px 12px' }}>
              <div style={{ height: '5px', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '3px', width: '60%', margin: '0 auto' }} />
            </div>
          </div>
        )}

        {id === 'propuesta_b' && (
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ borderTop: `4px solid ${primary}`, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
              {logo ? (
                <img src={logo} alt="logo" style={{ height: '24px', width: 'auto', objectFit: 'contain' }} />
              ) : (
                <div style={{ width: '24px', height: '24px', backgroundColor: primary, borderRadius: '3px', opacity: 0.3 }} />
              )}
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 'bold', fontSize: '11px', color: secondary }}>{name}</div>
                <div style={{ fontSize: '8px', color: '#888' }}>Taller Automotriz</div>
              </div>
            </div>
            <div style={{ flex: 1, padding: '8px 12px' }}>
              <div style={{ height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', marginBottom: '4px', width: '80%' }} />
              <div style={{ height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', marginBottom: '4px', width: '55%' }} />
              <div style={{ height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', width: '65%' }} />
            </div>
            <div style={{ padding: '6px 12px', borderTop: `2px solid ${primary}`, display: 'flex', justifyContent: 'space-between', fontSize: '8px', color: '#888' }}>
              <span>Contacto</span>
              <span style={{ color: primary }}>●</span>
              <span>Dirección</span>
            </div>
          </div>
        )}
      </div>

      {/* Card footer */}
      <div className={`px-4 py-3 flex items-center justify-between ${selected ? 'bg-primary/5' : 'bg-card'}`}>
        <div>
          <p className="text-sm font-semibold text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
          selected ? 'border-primary bg-primary' : 'border-border'
        }`}>
          {selected && <Icon name="Check" size={11} className="text-white" />}
        </div>
      </div>
    </div>
  );
};

// ─── Main PapeleriaTab ───────────────────────────────────────────────────────

const PapeleriaTab = ({
  form,
  onChange,
  onUploadLetterhead,
  onUploadBusinessCard,
  onRemoveLetterhead,
  onRemoveBusinessCard,
  uploadingLetterhead,
  uploadingBusinessCard,
}) => {
  const hasLetterhead = !!form?.letterhead_url;

  return (
    <div className="space-y-8">

      {/* Status banner */}
      {hasLetterhead ? (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-success/10 border border-success/30">
          <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-foreground">Papelería personalizada activa</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Tu hoja membretada subida será usada como plantilla oficial en cotizaciones, órdenes de trabajo y notas de venta.
              Las propuestas automáticas están desactivadas mientras tengas papelería propia.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-warning/10 border border-warning/30">
          <Icon name="Info" size={20} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-foreground">Sin papelería personalizada</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Sube tu hoja membretada para usarla como base oficial, o elige una de las 2 propuestas automáticas generadas con tu identidad visual.
            </p>
          </div>
        </div>
      )}

      {/* Letterhead upload */}
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Hoja membretada</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Si subes tu propia hoja membretada, esta tendrá <strong>prioridad máxima</strong> y se usará como base de todos los documentos.
          Las propuestas automáticas se ignorarán.
        </p>
        <FileUploadZone
          label="Subir hoja membretada"
          hint="Sube la imagen de tu hoja membretada oficial (PNG, JPG). Se usará como fondo/base del documento."
          currentUrl={form?.letterhead_url}
          onUpload={onUploadLetterhead}
          onRemove={onRemoveLetterhead}
          uploading={uploadingLetterhead}
          accept="image/*"
          isImage={true}
        />
      </div>

      {/* Business card upload */}
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Tarjeta de presentación</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Sube la imagen de tu tarjeta de presentación para tenerla disponible en el sistema.
        </p>
        <FileUploadZone
          label="Subir tarjeta de presentación"
          hint="PNG, JPG hasta 10MB"
          currentUrl={form?.business_card_url}
          onUpload={onUploadBusinessCard}
          onRemove={onRemoveBusinessCard}
          uploading={uploadingBusinessCard}
          accept="image/*"
          isImage={true}
        />
      </div>

      {/* Auto proposals — only shown when no custom letterhead */}
      {!hasLetterhead && (
        <div>
          <h3 className="text-base font-semibold text-foreground mb-1">Propuestas automáticas de diseño</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Elige una propuesta generada automáticamente con tu identidad visual. Se aplicará a todos los documentos mientras no tengas papelería propia subida.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProposalCard
              id="propuesta_a"
              label="Propuesta A — Banda de color"
              description="Encabezado con banda de color primario, logo y datos de contacto"
              selected={form?.selected_proposal === 'propuesta_a'}
              onSelect={(id) => onChange('selected_proposal', id)}
              branding={form}
            />
            <ProposalCard
              id="propuesta_b"
              label="Propuesta B — Línea superior"
              description="Línea de acento superior, logo a la izquierda, datos a la derecha"
              selected={form?.selected_proposal === 'propuesta_b'}
              onSelect={(id) => onChange('selected_proposal', id)}
              branding={form}
            />
          </div>
          {form?.selected_proposal && form?.selected_proposal !== 'none' && (
            <button
              type="button"
              onClick={() => onChange('selected_proposal', 'none')}
              className="mt-3 text-xs text-muted-foreground hover:text-foreground underline"
            >
              Quitar selección de propuesta
            </button>
          )}
        </div>
      )}

      {/* Billing data */}
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Datos de facturación</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Información fiscal que aparecerá en documentos oficiales y facturas.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="RFC fiscal"
            value={form?.billing_rfc || ''}
            onChange={(e) => onChange('billing_rfc', e?.target?.value)}
            placeholder="Ej. VERM690901CD6"
          />
          <Input
            label="Razón social"
            value={form?.billing_fiscal_name || ''}
            onChange={(e) => onChange('billing_fiscal_name', e?.target?.value)}
            placeholder="Nombre completo o razón social"
          />
          <Input
            label="Dirección fiscal"
            value={form?.billing_address || ''}
            onChange={(e) => onChange('billing_address', e?.target?.value)}
            placeholder="Calle, número, colonia, CP"
          />
          <Input
            label="Régimen fiscal"
            value={form?.billing_regime || ''}
            onChange={(e) => onChange('billing_regime', e?.target?.value)}
            placeholder="Ej. Régimen Simplificado de Confianza"
          />
          <Input
            label="Uso de CFDI"
            value={form?.billing_cfdi_use || ''}
            onChange={(e) => onChange('billing_cfdi_use', e?.target?.value)}
            placeholder="Ej. G03 - Gastos en general"
          />
        </div>
      </div>
    </div>
  );
};

export default PapeleriaTab;
